import React, { useEffect } from "react";
import {
    FormLayout,
    HeaderActions,
    Heading,
    LoadingProgress,
    Panel,
    Text,
    Textarea,
} from "@/ui";
import { Icon } from "@/components";
import { useFormActions, useForm, useMeta, useValidator } from "@/hooks";
import {
    MUTATION_UPDATE_MIN_ZIP_BY_CODE,
    QUERY_LIST_STORE_APPS,
} from "../../apollo/module-operations/app";
import omit from "lodash/omit";
import styles from "./GeneralsForm.module.scss";

const SaveIcon = () => <Icon icon="save" />;

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const MinZipByCodeForm = ({ app }) => {
    const { user } = useMeta();
    const { inputs, setInputs, inputChange } = useForm();

    useEffect(() => {
        const locations = app.locations
            ? app.locations.map(location => omit(location, "__typename"))
            : [];

        setInputs({
            private_key: app.private_key,
            locations: JSON.stringify(locations, null, 2),
        });
    }, [app]);

    const getLocations = () => {
        try {
            return JSON.parse(inputs?.locations);
        } catch (error) {
            return [];
        }
    };

    const options = {
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
            input: {
                ...app,
                ...inputs,
                locations: getLocations(),
            },
        },
    };

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "locations",
                method: "isJSON",
                validWhen: true,
                args: [],
                message: "Invalid locations structure.",
            },
        ],
        {
            ...inputs,
        },
    );

    const { createOrUpdate, loading } = useFormActions(
        "id",
        "MinZipByCode",
        "",
        MUTATION_UPDATE_MIN_ZIP_BY_CODE,
        [
            {
                query: QUERY_LIST_STORE_APPS,
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                },
            },
        ],
        options,
        "",
        isValid,
        isSubmited,
    );

    return (
        <Panel
            className={styles.root}
            header={
                <div className={styles.headerBar}>
                    <div className={styles.headerText}>
                        <Heading level={4}>Min By Code</Heading>
                        <Text tone="secondary" variant="body-sm">
                            Min By Code Information
                        </Text>
                    </div>
                    <HeaderActions
                        actions={[
                            {
                                id: "update",
                                label: "Update",
                                icon: loading ? undefined : <SaveIcon />,
                                loading,
                                disabled: loading,
                                onClick: () => createOrUpdate(),
                            },
                        ]}
                        aria-label="Min By Code actions"
                    />
                </div>
            }
            showHeaderDivider
        >
            {user.loading ? (
                <div className={styles.loading}>
                    <LoadingProgress
                        indeterminate
                        indicator="circular"
                        size="sm"
                        title="Loading"
                    />
                </div>
            ) : (
                <FormLayout>
                    <FormLayout.Section>
                        <Textarea
                            error={fieldError(validator, "locations")}
                            fullWidth
                            label="Locations"
                            name="locations"
                            onChange={inputChange}
                            rows={40}
                            value={inputs?.locations || "[]"}
                        />
                    </FormLayout.Section>
                </FormLayout>
            )}
        </Panel>
    );
};

export default MinZipByCodeForm;
